import { MIN_DATA_PATIENT } from "@/components/shared/util";
import { parseServerTime } from "@/filters/DateAndTime";
import { BodyData, CensusData, Patient } from "@/store/modules/patient/types";
import { TowerAction } from "@/store/modules/tower/actions";
import { Alert, CaseState, Reference, RiskFactor, Staff } from "@/store/types";
import _ from "lodash";
import { tryParseJSON } from "../components/shared/util";
import { demoFilter, demoSortBy } from "./demoFilterAndSort";
import Resolver, { CancelToken, http } from "./resolver";

export const enum ErrorTypes {
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NONE = 0,
  UNKNOWN = 1 // No status code
}

// One time map of vars
function mapVars(vars: any) {
  const caseStateMap: Record<string, CaseState> = {};
  vars.caseStates.forEach((cs: CaseState) => {
    caseStateMap[cs.state] = cs;
  });
  vars.caseState = caseStateMap;

  const riskMap: Record<string, Record<string, Array<RiskFactor>>> = {};
  vars.riskFactor.forEach((risk: RiskFactor) => {
    if (!risk.name || !risk.segment) {
      return;
    }
    if (!riskMap[risk.part]) {
      riskMap[risk.part] = {};
    }
    if (!riskMap[risk.part][risk.segment]) {
      riskMap[risk.part][risk.segment] = [];
    }
    if (risk.weight !== 0) {
      riskMap[risk.part][risk.segment].push(risk);
    }
  });
  vars.riskMap = riskMap;

  const referencesMap: Record<string, Record<string, Array<Reference>>> = {};
  vars.references.forEach((reference: Reference) => {
    if (!reference.category || !reference.name) {
      return;
    }
    if (!referencesMap[reference.category]) {
      referencesMap[reference.category] = {};
    }
    if (!referencesMap[reference.category][reference.part]) {
      referencesMap[reference.category][reference.part] = [];
    }
    referencesMap[reference.category][reference.part].push(reference);
  });
  vars.referencesMap = referencesMap;
  vars.editLimitsConfig = tryParseJSON(vars.logicRules.RuleSettings, []);
  vars.orderSetConfig = _.get(vars, "uiConfig.elements.OrderSetConfig", "");
  if (vars.orderSetConfig != "") {
    vars.orderSetConfig = tryParseJSON(vars.orderSetConfig, []);
  }
  return vars;
}

function hoursMinuteString(minutes: number): string {
  const hrs: number = Math.floor(minutes / 60);
  const mins: number = minutes % 60;
  return (hrs > 0 ? hrs + "h" : "") + (mins > 0 ? mins + "m" : "");
}

function getCaseStatus(census: CensusData, now: string): any {
  if (census.atoms.caseMinutes) {
    const serverTime = parseServerTime(now);
    const inRoom = parseServerTime(census.inRoom);
    const timeSpentInRoom = serverTime.diff(inRoom, "minute");
    const caseMinutes: number = census.atoms.caseMinutes.value;
    const displayDuration =
      hoursMinuteString(timeSpentInRoom) +
      " / " +
      hoursMinuteString(caseMinutes);
    const endTime = inRoom.add(caseMinutes, "minute").format("HH:mm");
    const inRoomPercent = Math.max(
      Math.round((timeSpentInRoom / caseMinutes) * 100),
      0
    );
    let percent = inRoomPercent;
    let color = "green";
    if (inRoomPercent > 115) {
      percent = 100;
      color = "red";
    } else if (inRoomPercent >= 100) {
      percent = 100;
      color = "yellow";
    }
    return {
      displayDuration,
      inRoomPercent,
      endTime,
      percent,
      color
    };
  }
  return null;
}

// This should be a configurable filter
function abbreviateStaff(census: CensusData): string {
  const sortedStaff = _.sortBy(census.staff, [
    (s: Staff) => s.role === "Attending" || s.role,
    "name"
  ]);
  return sortedStaff
    .filter(f => f.role !== "Perfusionist" && f.role !== "Test")
    .map(staff => {
      return staff.userName.split(",")[0].slice(0, 4);
    })
    .slice(0, 2)
    .join(" | ");
}

// Could be removed if server was doing this work
function augmentCensusList(census: Array<CensusData>, now: string): any {
  const augmentedCensus = census.map((census: CensusData) => {
    return augmentCensus(census, now);
  });
  return Object.freeze(augmentedCensus);
}
function splitAlertMessage(message: string) {
  const alertParts = message.replace(/&amp;/g, "&").split("^^");
  return { message: alertParts[0], audioMessage: alertParts[1] || "" };
}
function augmentCensus(census: CensusData, now: string) {
  census.roomEmpty = census.regNum === "";
  setType(census);
  census.caseStatus = getCaseStatus(census, now);
  census.abbreviatedStaff = abbreviateStaff(census);
  census.alert.forEach(alert => {
    Object.assign(alert, splitAlertMessage(alert.message));
  });
  census.maxAlertLevel = census.alert.reduce((acc: number, current: Alert) => {
    return current.level > acc ? current.level : acc;
  }, 0);
  return census;
}

function setType(census: CensusData) {
  census.type = census.type ? census.type : 0;
  census.minDataPatient = census.type === MIN_DATA_PATIENT;
}

function parseBodyResponse(response: any, id: number) {
  const censusData = response.data.census;
  // Remove census from body
  delete response.data.census;
  const body: BodyData = response.data;
  const census = augmentCensus(censusData, body.now);
  formatCaseReview(body);
  return Object.freeze({ id, body, census });
}

function formatCaseReview(body: BodyData) {
  if (body.atoms) {
    // Remove audio portion of alert from atom message
    Object.entries(body.atoms).forEach(([key, atom]) => {
      body.atoms[key].alert = splitAlertMessage(atom.alert).message;
    });
  }
}

function getFormat(view: string) {
  switch (view.toLowerCase()) {
    case "grid":
      return "Grid";
    case "list":
      return "Alerts";
    case "list review":
      return "review";
  }
}
let censusRequestCancel: any = null;

export default {
  async getCensus(
    area: string,
    view: string,
    unit: string,
    tower: boolean,
    filterAndSorting: any,
    caseStates: any
  ) {
    // cancel previous request
    if (censusRequestCancel) {
      censusRequestCancel("Canceling previous request");
      censusRequestCancel = null;
    }

    if (!unit) {
      unit = "All";
    }

    const format = getFormat(view);

    const filters = filterAndSorting.filters;
    const sortBy = filterAndSorting.sortBy;

    const censusUrl = Resolver.census;
    const params = { area, unit, tower, format, filters, sortBy };
    const cancelToken = new CancelToken(c => (censusRequestCancel = c));
    const response = await http({
      url: censusUrl,
      params: params,
      cancelToken
    });
    censusRequestCancel = null;

    // send network test data back to server for latency and validation
    const testID = response.data.testID;
    const isTesting = testID != null && testID !== "";
    if (isTesting) {
      const json = JSON.stringify(response.data);
      this.validateRequest(json, testID);
    }

    let patients: Array<Patient> = augmentCensusList(
      response.data.census,
      response.data.stats.now
    )
      .map((census: CensusData) => {
        return {
          id: +census.roomSys,
          census,
          body: {
            alertStatus: null,
            atoms: {},
            risk: [],
            census: null,
            current: null,
            graph: {},
            version: "",
            caseReview: null,
            postOpReview: null,
            now: "",
            testID: ""
          }
        };
      })
      .filter((p: any) => !tower || (tower && p.census.treatment !== -1));

    patients.forEach((patient: Patient) => {
      patient.census.caseStage = caseStates[patient.census.caseState].stage;
    });

    if (Resolver.demo) {
      patients = demoSortBy(demoFilter(patients, filters), sortBy);
    }

    return { patients, stats: response.data.stats, isTesting };
  },
  async accessRequest(
    data: Record<string, string> = {},
    params: Record<string, any> = {}
  ) {
    const formData = new FormData();
    Object.keys(data).forEach((key: string) => {
      formData.append(key, data[key]);
    });
    const response = await http({
      method: "post",
      url: Resolver.accessRequest,
      data: formData,
      params: params
    });
    return response.data;
  },
  async toggleMaskSetting(): Promise<boolean> {
    const response = await http({
      method: "get",
      url: Resolver.accessRequest,
      params: { action: "toggleMask" }
    });
    const mask: boolean = response.data.mask;
    return mask;
  },
  async getBody(id: number, area: string) {
    const response = await http({
      url: Resolver.data,
      params: { area: area, roomSys: id, action: "getCurrent" }
    });
    const json = JSON.stringify(response.data);
    const data = parseBodyResponse(response, id);
    if (data.body.testID != null && data.body.testID != "") {
      this.validateRequest(json, data.body.testID);
    }
    return data;
  },
  async getCareCoordinationNote(id: number) {
    const response = await http({
      url: Resolver.data,
      params: { roomSys: id, action: "getCareNote" }
    });
    const careNote = response.data;
    return careNote;
  },
  async getSystem() {
    const response = await http.get(Resolver.system);
    return response.data;
  },
  async getVars(area: string) {
    const result = await http({
      url: Resolver.vars,
      params: { area }
    });
    return Object.freeze(mapVars(result.data));
  },
  async updateSettings(
    area: string,
    id: number,
    userID: string,
    settings: Record<string, string>
  ) {
    const data = Object.assign({}, settings, {
      area,
      action: "updateSettings",
      roomSys: id,
      userId: userID
    });

    const response = await http({
      method: "post",
      url: Resolver.data,
      params: data
    });

    return parseBodyResponse(response, id);
  },
  async redirectRequest(id: number) {
    const response = await http({
      method: "post",
      url: Resolver.redirectRequest,
      params: { roomSys: id }
    });

    return response.data;
  },

  async logRequest(
    params: Record<string, string>,
    formData: Record<string, any>
  ) {
    const data = new FormData();
    Object.keys(formData).forEach((key: string) => {
      data.append(key, formData[key]);
    });
    let req = {
      method: "post",
      url: Resolver.logRequest,
      data,
      params
    };
    const response = await http(req);
    return response.data;
  },
  async towerRequest(formData: Record<string, any>, action: TowerAction) {
    formData.action = action;
    const data = new FormData();
    Object.keys(formData).forEach((key: string) => {
      data.append(key, formData[key]);
    });

    const response = await http({
      method: "post",
      url: Resolver.towerRequest,
      data
    });
    if (response.data && response.data.caseInfo) {
      response.data.caseInfo = parseBodyResponse(
        { data: response.data.caseInfo },
        response.data.caseInfo.census.roomSys
      );
    }
    return response.data;
  },
  async verifyRequest(data: Record<string, any>) {
    const response = await http({
      method: "post",
      url: Resolver.verifyRequest,
      params: data
    });

    return response.data;
  },

  //
  // wireless testing validation ------------------------------
  async validateStartRequest(note: string) {
    const formData = new FormData();
    formData.append("notes", encodeURIComponent(note));

    const response = await http({
      method: "post",
      url: Resolver.validateRequest,
      params: { action: "start" },
      data: formData
    });

    return response.data;
  },

  async validateAddNote(note: string) {
    const formData = new FormData();
    formData.append("notes", encodeURIComponent(note));
    const response = await http({
      method: "post",
      url: Resolver.validateRequest,
      data: formData,
      params: { action: "addNote" }
    });
    return response.data;
  },

  async validateReceivedPage(index: number) {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "pageReceived", index: index }
    });
    return response.data;
  },

  async validateRequest(data: string, id: string) {
    const formData = new FormData();
    formData.append("data", encodeURIComponent(data));
    const response = await http({
      method: "post",
      url: Resolver.validateRequest,
      data: formData,
      params: { action: "test", id: id }
    });
    return response.data;
  },

  async validateStatus() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "results" }
    });
    return response.data;
  },

  async validateStopRequest() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "stop" }
    });
    return response.data;
  },

  async validateStartPagingRequest(pagerID: string) {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "startPaging", pagerID: encodeURIComponent(pagerID) }
    });
    return response.data;
  },

  async validatePagingResults() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "pagingResults" }
    });
    return response.data;
  },

  async validateAddPagingNote(note: string) {
    const formData = new FormData();
    formData.append("notes", encodeURIComponent(note));
    const response = await http({
      method: "post",
      url: Resolver.validateRequest,
      data: formData,
      params: { action: "addPagingNote" }
    });
    return response.data;
  },

  async validateCompletePagingRequest() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "completePagingTest" }
    });
    return response.data;
  },

  async validateStopPagingRequest() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "stopPaging" }
    });
    return response.data;
  },

  async validateToggleActiveRequest() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "toggleActiveCase" }
    });
    return response.data;
  },

  async validateStartActiveRequest(area: string, roomSys: number) {
    // const formData = new FormData();
    // formData.append("message", message);

    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "startActiveCaseTest", area, roomSys }
    });
    return response.data;
  },

  async validateCurrentActiveCasesRequest() {
    const response = await http({
      method: "get",
      url: Resolver.validateRequest,
      params: { action: "currentActiveCaseTests" }
    });
    return response.data;
  },

  // query to get list of med data
  async getMedData(area: string, roomSys: number) {
    const response = await http({
      method: "get",
      url: Resolver.data,
      params: { action: "meds", area, roomSys }
    });
    return response.data;
  },

  async getPerformance(time: string = "") {
    const response = await http({
      method: "get",
      url: Resolver.adminRequest,
      params: { action: "performance", time }
    });
    return response.data;
  },

  async getComments() {
    const response = await http({
      method: "get",
      url: Resolver.adminRequest,
      params: { action: "comments" }
    });
    return response.data;
  },

  async errorSummary() {
    const response = await http({
      method: "get",
      url: Resolver.adminRequest,
      params: { action: "errorSummary" }
    });
    return response.data;
  },

  async getCommentData(logID: number) {
    const response = await http({
      method: "get",
      url: Resolver.adminRequest,
      params: { action: "loadComment", logID }
    });
    return response.data;
  }
};
